import React, { useEffect, useMemo, useState } from "react";
import { Select, Tooltip } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { apiRequest } from "util/services";
import * as actions from "redux/newDashboard/actions.js";
import itemStyles from "../CreationItem/style.module.scss";
import styles from "./style.module.scss";

const PENDING_RESTRICTED_INDUSTRIES = [
  { id: null, name: "telecom", allowed: false },
  { id: null, name: "retail", allowed: false },
];

const isOthersIndustry = (industry) =>
  industry?.name === "Others" || industry?.id === -1;

const isAllowedIndustry = (industry) =>
  Boolean(industry) &&
  industry.allowed !== false &&
  !isOthersIndustry(industry);

/**
 * Industry / category picker for dashboard creation step 1.
 * Persists selection to newDashboardReducer.industry_id.
 */
const IndustryCategorySelect = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { industry_id: industryId } = useSelector(
    (state) => state.newDashboardReducer
  );
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    const load = async () => {
      const res = await apiRequest("dashboards/models", "POST");
      if (!res?.models?.length) return;
      const apiNames = new Set(
        res.models.map((item) => (item.name || "").toLowerCase())
      );
      const pending = PENDING_RESTRICTED_INDUSTRIES.filter(
        (item) => !apiNames.has(item.name.toLowerCase())
      );
      const merged = [...res.models, ...pending];
      setIndustries(merged);

      const currentIndustry = res.models.find((item) => item.id === industryId);
      const hasValidSelection =
        currentIndustry && isAllowedIndustry(currentIndustry);
      if (hasValidSelection) return;

      const defaultIndustry =
        res.models.find(
          (item) => item.id === res?.default?.id && isAllowedIndustry(item)
        ) || res.models.find((item) => isAllowedIndustry(item));
      if (defaultIndustry?.id) {
        dispatch({
          type: actions.UPDATE_NEW_Dashboard,
          payload: { industry_id: defaultIndustry.id },
        });
      }
    };
    load();
  }, [dispatch, industryId]);

  const selectedIndustry = useMemo(
    () => industries.find((item) => item.id === industryId),
    [industries, industryId]
  );

  const { availableIndustries, restrictedIndustries } = useMemo(() => {
    const available = [];
    const restricted = [];
    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    for (const industry of industries) {
      if (isOthersIndustry(industry)) continue;
      if (industry.allowed === false) restricted.push(industry);
      else available.push(industry);
    }
    available.sort(byName);
    restricted.sort(byName);
    return { availableIndustries: available, restrictedIndustries: restricted };
  }, [industries]);

  const categorySelectOptions = useMemo(() => {
    const toOption = (industry) => ({
      value: industry.name,
      label: industry.name,
      title: industry.name,
      allowed: industry.allowed !== false,
      id: industry.id,
      className:
        industry.allowed === false ? "category-option-restricted" : undefined,
    });
    const groups = [];
    if (availableIndustries.length > 0) {
      groups.push({
        label: t("new_dashboard.69").toUpperCase(),
        options: availableIndustries.map(toOption),
      });
    }
    if (restrictedIndustries.length > 0) {
      groups.push({
        label: t("new_dashboard.70").toUpperCase(),
        options: restrictedIndustries.map(toOption),
      });
    }
    return groups;
  }, [availableIndustries, restrictedIndustries, t]);

  const handleChange = (value) => {
    const selected = industries.find((industry) => industry.name === value);
    if (!selected || !isAllowedIndustry(selected)) return;
    dispatch({
      type: actions.UPDATE_NEW_Dashboard,
      payload: { industry_id: selected.id },
    });
  };

  const renderCategoryOption = (option) => {
    const isRestricted = option.data?.allowed === false;
    const label = option.label ?? option.data?.label ?? "";
    const optionRow = (
      <div
        className={`${styles.categoryOption} ${
          isRestricted ? styles.categoryOptionRestricted : ""
        }`}
        role="option"
        aria-selected={
          option.value === selectedIndustry?.name ? "true" : "false"
        }
        aria-disabled={isRestricted ? "true" : undefined}
        onMouseDown={(event) => {
          if (isRestricted) event.preventDefault();
        }}
      >
        <span className={styles.categoryOptionLabel}>{label}</span>
        {isRestricted && (
          <LockOutlined className={styles.categoryLockIcon} aria-hidden="true" />
        )}
      </div>
    );
    if (isRestricted) {
      return (
        <Tooltip
          title={t("new_dashboard.71")}
          placement="right"
          overlayClassName="new-dashboard-category-dropdown"
          mouseEnterDelay={0.2}
        >
          <span className={styles.categoryOptionTooltipWrap}>{optionRow}</span>
        </Tooltip>
      );
    }
    return optionRow;
  };

  const selectValue =
    selectedIndustry && isAllowedIndustry(selectedIndustry)
      ? selectedIndustry.name
      : undefined;

  return (
    <div className={itemStyles.container} id="industry_category_select">
      <div className={itemStyles.label}>
        <h5>{t("new_dashboard.4")}</h5>
        <h6>{t("new_dashboard.5")}</h6>
      </div>
      <div className={itemStyles.content} id="custom_dropdown">
        <Select
          onChange={handleChange}
          value={selectValue}
          options={categorySelectOptions}
          optionRender={renderCategoryOption}
          style={{ width: "100%", maxWidth: 400, height: 40 }}
          showSearch
          optionFilterProp="title"
          filterOption={(input, option) => {
            const label = (option?.label ?? option?.title ?? "").toString();
            return label.toLowerCase().includes(input.toLowerCase());
          }}
          popupClassName="new-dashboard-category-dropdown"
          listHeight={320}
          onInputKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            const active = document.querySelector(
              ".new-dashboard-category-dropdown .ant-select-item-option-active"
            );
            if (active?.classList.contains("category-option-restricted")) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        />
      </div>
    </div>
  );
};

export default IndustryCategorySelect;
